import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  createApiClient,
  listApiScopes,
  ApiClientCreatedSecret,
  ApiClientScope,
  ApiScopeGroup,
} from '../services/apiClientService';
import ScopeTreeCheckboxes from './ScopeTreeCheckboxes';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateApiClientModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [tree, setTree] = useState<ApiScopeGroup[]>([]);
  const [checked, setChecked] = useState<Set<ApiClientScope>>(new Set<ApiClientScope>(['tariff:classify']));
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ApiClientCreatedSecret | null>(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    listApiScopes().then(setTree).catch((e) => setErr(e?.message || 'Failed to load scopes'));
  }, [open]);

  const reset = () => {
    setName('');
    setChecked(new Set<ApiClientScope>(['tariff:classify']));
    setCreated(null);
    setSavedConfirmed(false);
    setErr(null);
  };

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const c = await createApiClient(name.trim(), Array.from(checked));
      setCreated(c);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (created && !savedConfirmed) return; // force confirm
    onClose();
    reset();
    if (created) onCreated();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Create API Client</DialogTitle>
      <DialogContent>
        {!created && (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Client name (visible to admins only)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            <Typography variant="subtitle2">Scopes</Typography>
            <ScopeTreeCheckboxes tree={tree} checked={checked} onChange={setChecked} />
            {err && <Alert severity="error">{err}</Alert>}
          </Box>
        )}

        {created && (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Alert severity="warning">
              <strong>Save the client secret now.</strong> It will not be shown again.
              If you lose it, you must rotate the secret to regenerate one.
            </Alert>

            <ReadOnlyField label="client_id" value={created.client_id} />
            <ReadOnlyField label="client_secret" value={created.client_secret} mono />

            <FormControlLabel
              control={
                <Checkbox
                  checked={savedConfirmed}
                  onChange={(_, v) => setSavedConfirmed(v)}
                />
              }
              label="I have saved the client_secret in a secure location."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!created && (
          <>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              variant="contained"
              disabled={busy || !name.trim() || checked.size === 0}
            >
              Create
            </Button>
          </>
        )}
        {created && (
          <Button onClick={close} variant="contained" disabled={!savedConfirmed}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ReadOnlyField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const copy = () => navigator.clipboard?.writeText(value);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={value}
          fullWidth
          InputProps={{
            readOnly: true,
            sx: mono ? { fontFamily: 'monospace' } : undefined,
          }}
          size="small"
        />
        <IconButton onClick={copy} aria-label={`Copy ${label}`}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
